import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

// Float Activation Item Component
const FloatActivationItem: React.FC<{
  image: string;
  index: number;
}> = ({ image, index }) => {
  const [cardRef, cardVisible] = useScrollAnimation(0.2);

  return (
    <div 
      ref={cardRef}
      className={`group relative overflow-hidden rounded-xl transition-all duration-500 hover:shadow-2xl hover:shadow-brand-orange/20 hover:-translate-y-2 ${
        cardVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-10'
      }`}
      style={{ 
        animationDelay: `${index * 100}ms`,
        transitionDelay: `${index * 100}ms`
      }}
    >
      {/* Image Container */}
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img 
          src={image} 
          alt="Float Activation Campaign"
          className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110"
        />

        {/* Float Activation Floating Elements */}
        <div className="absolute top-4 right-4 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:animate-bounce">
          <span className="text-white text-sm">FA</span>
        </div>
        <div className="absolute top-4 left-4 w-6 h-6 bg-brand-orange/80 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700 group-hover:animate-pulse"></div>
      </div>
    </div>
  );
};

const FloatActivationPage: React.FC = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.3);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [statsRef, statsVisible] = useScrollAnimation(0.4);

  // Float Activation images from the folder - All 13 images
  const floatActivationImages = [
    "/Float Activation/11910877-17ba-4bcc-a531-9cab69b1cb2b.jpg",
    "/Float Activation/154a3724-957b-4e10-a17a-bf23c3c5bac7.jpg",
    "/Float Activation/19a74da4-6390-4029-aa5e-46c60da16ee6.jpg",
    "/Float Activation/1eefec51-22dd-42ea-8e0b-cb29c425b6d3.jpg",
    "/Float Activation/2178479a-16a3-45e7-9a6c-fddd7b6617fe.jpg",
    "/Float Activation/5376e472-2b1a-4cbb-817e-602524dc17d1.jpg",
    "/Float Activation/76e023e3-465f-40d7-8282-f6808a819792.jpg",
    "/Float Activation/820e3619-8021-4077-b14d-2adff313047a.jpg",
    "/Float Activation/9cc238ad-2c52-44ca-8695-aa93df44af27.jpg",
    "/Float Activation/b9e5a401-386e-43e4-a253-b97f7b63fb23.jpg",
    "/Float Activation/be48f591-73ea-4441-9509-50d8aa5917f1.jpg",
    "/Float Activation/c2e66fed-5c34-45e2-8ed1-a8b7b22cde29.jpg",
    "/Float Activation/e6b8b2c0-1455-4839-bcad-d5ec82e38390.jpg"
  ];

  const stats = [
    { number: '13', label: 'Float Activations', icon: '🎈' },
    { number: '8+', label: 'Cities', icon: '🏙️' },
    { number: '100%', label: 'Sky Coverage', icon: '☁️' },
    { number: '24/7', label: 'Support', icon: '🚀' }
  ];

  return (
    <div className="pt-32 min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-slate-900 via-orange-900 to-slate-900">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-600/10 via-red-600/10 to-yellow-600/10"></div>
          <div className="absolute top-20 left-20 w-32 h-32 bg-orange-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            ref={headerRef}
            className={`text-center transition-all duration-1000 ${
              headerVisible 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-white text-sm font-medium mb-6">
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
              <span>Aerial Marketing</span>
            </div>

            {/* Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                Float
              </span>
              <br />
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                Activation
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl sm:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-8">
              Explore our innovative float activation campaigns, aerial marketing solutions, and sky-high brand experiences that capture attention from above and create unforgettable moments.
            </p>

            {/* Stats */}
            <div 
              ref={statsRef}
              className={`grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto transition-all duration-1000 ${
                statsVisible 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-10'
              }`}
            >
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.number}</div>
                  <div className="text-sm sm:text-base text-white/80">{stat.label}</div>
                  <div className="text-2xl mt-2">{stat.icon}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Float Activation Gallery */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-gray-900 via-brand-primary to-gray-900 bg-clip-text text-transparent">
                Float Activation Gallery
              </span>
            </h2>
            <div className="w-16 sm:w-24 h-1 bg-gradient-to-r from-brand-orange to-brand-gold mx-auto mb-4"></div>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              From sky-high brand displays to aerial marketing campaigns, discover how our float activations create memorable experiences that reach audiences from above.
            </p>
          </div>

          {/* Float Activation Images Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {floatActivationImages.map((image, index) => (
              <FloatActivationItem 
                key={index} 
                image={image} 
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-brand-primary to-brand-orange">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Take Your Brand to New Heights?
          </h3>
          <p className="text-xl text-white/90 mb-8">
            Let's create an unforgettable float activation experience that captures attention from above and drives your brand skyward.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/quote"
              className="bg-white text-brand-orange px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg hover:shadow-white/25 hover:scale-105 group"
            >
              <span className="flex items-center justify-center space-x-2">
                <span>Get Quote</span>
                <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </Link>
            <Link 
              to="/"
              className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:text-brand-orange transition-all duration-300 hover:shadow-lg hover:shadow-white/25 hover:scale-105 group"
            >
              <span className="flex items-center justify-center space-x-2">
                <span>Back to Home</span>
                <svg className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FloatActivationPage;
